import type { MutableState } from '../State';
import { ProductId, ProductRewardType } from '.';
import * as Iap from '.';
import { shopBundlesClaimProductRewards } from '../match2/shopbundles/match2-shopbundles.modifiers';
import { match2BundleSaleClaimProductRewards } from '../match2/bundlesale/match2-bundlesale.modifiers';
import * as Inventory from '../inventory';
import { CurrencyReplicantAPI } from '../inventory/inventory.utils';
import { sendPurchaseConfirmationOA } from './iap.chatbot';

type ClaimProductRewardsOptions = {
  sendPurchaseConfirmation?: boolean;
};

export function claimProductRewards(
  state: MutableState,
  rewards: Partial<Record<ProductRewardType, number>>,
  productId: ProductId,
  purchaseID: string,
  api: CurrencyReplicantAPI,
  opts: ClaimProductRewardsOptions = {},
): void {
  if (rewards.paidGems) {
    Inventory.modifiers.addPaidGems(
      state,
      rewards.paidGems,
      {
        feature: 'gems_product',
        $subFeature: productId as string,
      },
      api,
      {
        purchaseID,
      },
    );
  }
  if (rewards.freeGems) {
    Inventory.modifiers.addFreeGems(
      state,
      rewards.freeGems,
      {
        feature: 'gems_product',
        $subFeature: productId as string,
      },
      api,
      {
        purchaseID,
      },
    );
  }

  // Claim products rewards for features, generally including boosters, powerups, etc.
  // IMPORTANT: These calls were originally in same places that Iap.modifiers.claimProductRewards() was being called
  // and it was too easy to forget to add all of them, so they have been moved here.
  shopBundlesClaimProductRewards(state, productId, api, { purchaseID });
  match2BundleSaleClaimProductRewards(state, productId, api, { purchaseID });

  if (opts.sendPurchaseConfirmation) {
    sendPurchaseConfirmationOA(state, { productId, purchaseID }, api);
  }
}

export function fulfillPurchase(
  state: MutableState,
  productId: ProductId,
  purchaseID: string,
  api: CurrencyReplicantAPI,
  opts: ClaimProductRewardsOptions = {},
): number {
  if (!Iap.ruleset.productIds.includes(productId)) {
    throw new Error('Unknown product: ' + productId);
  }

  const rewards = Iap.utils.getProductRewards(productId);
  const productPrice = Iap.ruleset.webIAPPrices[productId];

  claimProductRewards(state, rewards, productId, purchaseID, api, opts);

  // TODO: figure out actualLTV
  state.actualLTV += productPrice;
  state.rulesetLTV += productPrice;

  return productPrice;
}

/**
 * Used for IAP component to fulfill purchases
 */
export function fulfillPurchaseV2(
  state: MutableState,
  payment: {
    productId: ProductId;
    productPrice: number; // In JPY
    purchaseId: string;
  },
  api: CurrencyReplicantAPI,
  opts: ClaimProductRewardsOptions = {},
): number {
  const { productId, productPrice, purchaseId } = payment;
  const rewards = Iap.utils.getProductRewards(productId);

  claimProductRewards(state, rewards, productId, purchaseId, api, opts);

  // We should use getRevenue() result here.
  // It will work for OpenWeb because all prices are in JPY.
  // But for native we need to convert it to JPY
  state.actualLTV += productPrice; // In product price in JPY
  state.rulesetLTV += productPrice; // In product price in JPY

  return productPrice;
}
